import { createFileRoute, Link } from "@tanstack/react-router";
import { ShoppingCart, Trash2, ArrowRight, ArrowLeft, Plus, Minus } from "lucide-react";
import { useCart } from "@/lib/cart/cart-store";
import { useQuery } from "@tanstack/react-query";
import { getUsdZarRate } from "@/lib/api/fx.functions";

export const Route = createFileRoute("/cart")({
  head: () => ({
    meta: [
      { title: "Your cart | Nkadimeng Danny Legal" },
      { name: "description", content: "Review the study notes and legal documents in your cart before checking out securely with PayFast." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CartPage,
});

function CartPage() {
  const { rows, totalZar, count, hydrated, remove, setQty } = useCart();
  const { data: fx } = useQuery({
    queryKey: ["fx", "usd-zar"],
    queryFn: () => getUsdZarRate(),
    staleTime: 6 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const totalUsd = fx?.rate ? totalZar / fx.rate : null;

  return (
    <section className="container-narrow py-12">
      <Link to="/contracts" className="inline-flex items-center gap-1 text-sm text-navy hover:text-gold">
        <ArrowLeft className="h-3.5 w-3.5" /> Continue shopping
      </Link>
      <h1 className="mt-3 font-display text-3xl text-navy md:text-4xl">Your cart</h1>

      {!hydrated ? (
        <p className="mt-6 text-sm text-muted-foreground">Loading your cart…</p>
      ) : rows.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-border bg-card p-10 text-center shadow-soft">
          <ShoppingCart className="mx-auto h-10 w-10 text-muted-foreground" />
          <h2 className="mt-4 font-display text-xl text-navy">Your cart is empty</h2>
          <p className="mt-2 text-sm text-muted-foreground">Browse our study packs to add items.</p>
          <Link
            to="/contracts"
            className="mt-5 inline-flex items-center gap-2 rounded-md bg-navy px-5 py-2.5 text-sm font-semibold text-navy-foreground hover:bg-navy/90"
          >
            Browse contracts <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      ) : (
        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_360px]">
          <div className="space-y-3">
            {rows.map((r) => (
              <div
                key={`${r.itemId}-${r.topic ?? ""}`}
                className="flex flex-wrap items-start gap-4 rounded-xl border border-border bg-card p-5 shadow-soft"
              >
                <div className="flex-1 min-w-[200px]">
                  <h3 className="font-display text-lg text-navy">{r.item.name}</h3>
                  {r.topic && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      For: <span className="font-medium text-navy">{r.topic}</span>
                    </p>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    Turnaround: {r.item.turnaround} · R{r.item.priceZar} each
                  </p>
                </div>
                <div className="flex items-center gap-2 rounded-md border border-input">
                  <button
                    type="button"
                    aria-label="Decrease quantity"
                    onClick={() => setQty(r.itemId, r.topic, r.qty - 1)}
                    className="p-1.5 hover:bg-secondary disabled:opacity-40"
                    disabled={r.qty <= 1}
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="min-w-[24px] text-center text-sm font-semibold">{r.qty}</span>
                  <button
                    type="button"
                    aria-label="Increase quantity"
                    onClick={() => setQty(r.itemId, r.topic, r.qty + 1)}
                    className="p-1.5 hover:bg-secondary"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="text-right">
                  <p className="font-display text-lg text-navy">R{r.lineTotalZar}</p>
                  <button
                    type="button"
                    onClick={() => remove(r.itemId, r.topic)}
                    className="mt-1 inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
                  >
                    <Trash2 className="h-3 w-3" /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          <aside className="h-fit rounded-2xl border border-gold/40 bg-card p-6 shadow-soft">
            <h2 className="font-display text-xl text-navy">Order summary</h2>
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Items</dt>
                <dd className="font-medium">{count}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Subtotal</dt>
                <dd className="font-medium">R{totalZar}</dd>
              </div>
              <div className="flex justify-between border-t border-border pt-2">
                <dt className="font-semibold text-navy">Total (ZAR)</dt>
                <dd className="font-display text-xl text-navy">R{totalZar}</dd>
              </div>
              {totalUsd && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <dt>Charged in USD (today's rate R{fx!.rate.toFixed(2)}/$1)</dt>
                  <dd>≈ ${totalUsd.toFixed(2)}</dd>
                </div>
              )}
            </dl>
            <Link
              to={"/checkout" as any}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-navy px-5 py-3 text-sm font-semibold text-navy-foreground hover:bg-navy/90"
            >
              Checkout securely <ArrowRight className="h-4 w-4" />
            </Link>
            <p className="mt-3 text-[11px] text-muted-foreground">
              Pay securely with PayFast — card, Instant EFT, SnapScan, Zapper or
              PayFast wallet. Your notes are prepared and emailed within the
              turnaround time after PayFast confirms payment.
            </p>
          </aside>
        </div>
      )}
    </section>
  );
}
